import React, { ReactElement, ReactNode, forwardRef, useEffect, useRef, useState } from 'react';
import { Popper } from '@mui/base';
import ClickAwayListener from '@mui/base/ClickAwayListener';
import { MUIStyledCommonProps, styled } from '@mui/system';
import useKeyDown from './use-key-down';

import './index.scss';
import { useMemo } from 'react';
import { useCallback } from 'react';

export type PopoverPlacement =
  | 'auto-end'
  | 'auto-start'
  | 'auto'
  | 'bottom-end'
  | 'bottom-start'
  | 'bottom'
  | 'left-end'
  | 'left-start'
  | 'left'
  | 'right-end'
  | 'right-start'
  | 'right'
  | 'top-end'
  | 'top-start'
  | 'top';
interface PopoverProps {
  tooltipContent?: string;
  placement?: PopoverPlacement;
  withArrow?: boolean;
  withBorders: boolean;
  customContent?: ReactNode;
  htmlContent?: ReactNode;
  children: ReactElement;
  onClose?: () => void;
  trigger?: 'click' | 'hover';
  maxWidth?: number;
  hideOnMouseOut?: boolean;
  className?: string;
  activeOpenClass?: string;
}

interface PopoverStyledProps extends MUIStyledCommonProps {
  ownerState?: Pick<PopoverProps, 'withArrow' | 'withBorders'>;
}

const StyledPopperDiv = styled('div', {
  name: 'root',
  slot: 'popover',
})<PopoverStyledProps>(({ ownerState }) => ({
  boxShadow: '0 6px 8px 0 rgb(13 13 13 / 10%)',
  backgroundColor: 'rgb(255, 255, 255)',
  borderRadius: '3px',
  fontSize: '14px',
  margin: ownerState.withArrow ? '10px' : '2px',
  padding: '16px',
  ...(ownerState.withBorders && {
    border: '1px solid rgb(221, 221, 221)',
  }),
}));

const Root = styled('div', {
  name: 'root',
})(() => ({
  display: 'inline',
}));

const StyledPopperArrow = styled('span', {
  name: 'arrow',
  slot: 'popover',
})<PopoverStyledProps>(({ ownerState }: PopoverStyledProps) => ({
  ...(ownerState.withBorders && {
    border: '1px solid rgb(221, 221, 221)',
  }),
}));

const Popover = forwardRef<HTMLDivElement, PopoverProps>(function Popover(props, ref) {
  const {
    children,
    tooltipContent,
    placement = 'left',
    withArrow,
    withBorders,
    onClose,
    customContent,
    htmlContent,
    trigger = 'click',
    maxWidth = 'auto',
    hideOnMouseOut = true,
    className,
    activeOpenClass,
    ...other
  } = props;
  const parentRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const styleProps = {
    placement,
    withArrow,
    withBorders,
  };

  const cloneClick = (e) => {
    setAnchorEl(e.target);
    setOpen(!open);
  };

  const cloneMouseover = (e) => {
    if (activeOpenClass) {
      parentRef.current.classList.add(activeOpenClass);
    }
    setAnchorEl(parentRef.current);
    setOpen(true);
  };

  const cloneMouseout = (e) => {
    parentRef.current.classList.remove(activeOpenClass);
    if (hideOnMouseOut) {
      setAnchorEl(null);
      setOpen(false);
    }
  };

  const HandelTriggerProps = useMemo(() => {
    const funcMap = {
      click: {
        onClick: cloneClick,
      },
      hover: {
        onMouseEnter: cloneMouseover,
      },
    };

    return funcMap[trigger];
  }, [cloneClick, cloneMouseout, cloneMouseover, trigger]);

  const cloneProps = useMemo(
    () => ({
      ...children.props,
      ...HandelTriggerProps,
    }),
    [children, HandelTriggerProps]
  );

  const onClickAway = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    return () => {
      setAnchorEl(null);
    };
  }, []);

  useKeyDown(
    {
      target: anchorEl,
      key: 'Escape',
    },
    () => {
      setOpen(false);
    }
  );
  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <Root onMouseLeave={cloneMouseout} className={className} ref={parentRef}>
        {React.cloneElement(children, cloneProps)}
        {htmlContent === undefined && (
          <Popper
            {...other}
            placement={placement}
            anchorEl={anchorEl}
            open={!!anchorEl && open}
            ref={ref}
            style={{ maxWidth }}
            className="popover-root z-[9999]"
          >
            {customContent || (
              <StyledPopperDiv ownerState={styleProps}>
                {withArrow && <StyledPopperArrow ownerState={styleProps} className="arrow"></StyledPopperArrow>}
                {tooltipContent}
              </StyledPopperDiv>
            )}
          </Popper>
        )}
        {htmlContent && (
          <Popper
            {...other}
            placement={placement}
            anchorEl={anchorEl}
            open={!!anchorEl && open}
            ref={ref}
            style={{ maxWidth }}
            className="popover-root z-[9999]"
          >
            {htmlContent || (
              <StyledPopperDiv ownerState={styleProps}>
                {withArrow && <StyledPopperArrow ownerState={styleProps} className="arrow"></StyledPopperArrow>}
                <p dangerouslySetInnerHTML={{ __html: htmlContent }}></p>
              </StyledPopperDiv>
            )}
          </Popper>
        )}
      </Root>
    </ClickAwayListener>
  );
});

export { Popover };
